import { Employee } from '../types/Employee/Employee'
import { WorkItemInfo } from '../types/Work/WorkItemInfo'
import { WorkItem } from '../types/Work/WorkItem'
import { getAll, post, deleteOne, getOne } from './CommonServices/httpService'
import {
	getDirectReportsWorkItemsFB as getLaborWorkItemsFB,
	getForemanWorkItemsFB,
	getWorkItemInfoFB,
	assignTaskFB,
	unassignTaskFB,
	activateTaskFB,
	deactivateTaskFB,
} from './CacheFallbackServices/backlogFallback'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import { mapForemanWorkItems, mapLaborWorkItems } from './CacheMapperServices/backlogMapper'
import { hasConnection } from './CommonServices/healthGate'
import { showErrorToast } from './CommonServices/alertServices'

const getAssignedForemen = async (): Promise<Employee[]> => {
	const result = await getAll<Employee[]>(`/Workers/Backlog`, 'Failed to get backlog foremen')

	return searchBranch(result?.data ?? [])
}

function searchBranch(employee: Employee[]): Employee[] {
	if (!employee) {
		return []
	}
	return employee?.reduce((acc: Employee[], child: Employee) => {
		if (child.role.title === 'Foreman') {
			acc.push(child)
		}
		if (child.directReports && child?.directReports?.length > 0) {
			acc.push(...searchBranch(child?.directReports))
		}
		return acc
	}, [])
}

/** Online, the top level only (children load per row); offline, the cached full tree. */
const getForemanBacklog = async (
	id: number,
	db: ForemanDB | null,
	searchText = '',
): Promise<{ employee: Employee; directReports: Employee[]; fullTree: boolean }> => {
	const [{ employee, fullTree }, directReports] = await Promise.all([
		getForemanBacklogWorkItems(id, db, searchText),
		getLaborWorkItems(id, db),
	])
	if (!fullTree && db) refreshForemanBacklogCache(id, db)

	return {
		employee: employee,
		directReports: directReports,
		fullTree,
	}
}

/** The foreman's own work items: online the top level only, offline the cached full tree. */
const getForemanBacklogWorkItems = async (
	id: number,
	db: ForemanDB | null,
	searchText = '',
): Promise<{ employee: Employee; fullTree: boolean }> => {
	const fullTree = !(await hasConnection()) && !!db
	const employee = await (fullTree
		? getForemanWorkItems(id, db)
		: getForemanBacklogRoots(id, searchText))
	return { employee, fullTree }
}

const getForemanBacklogRoots = async (id: number, searchText = ''): Promise<Employee> => {
	const search = searchText.trim()
	const result = await getOne<Employee>(
		`/Workers/${id}/WorkItems/Backlog/Roots${
			search ? `?searchText=${encodeURIComponent(search)}` : ''
		}`,
		'Failed to get foreman work items.',
	)
	return result.data
}

const getForemanBacklogChildren = async (id: number, workItemId: number): Promise<WorkItem[]> => {
	const result = await getAll<WorkItem[]>(
		`/Workers/${id}/WorkItems/Backlog/${workItemId}/Children`,
		'Failed to get work item children.',
	)
	return result.data ?? []
}

let cacheRefresh: Promise<void> | undefined
let queuedCacheRefresh: { id: number; db: ForemanDB } | undefined

/**
 * One download at a time; requests made meanwhile collapse into one more after it, so an
 * older download can't overwrite a newer copy.
 */
const refreshForemanBacklogCache = (id: number, db: ForemanDB): Promise<void> => {
	if (cacheRefresh) {
		queuedCacheRefresh = { id, db }
		return cacheRefresh
	}
	cacheRefresh = getOne<Employee>(`/Workers/${id}/WorkItems/Backlog/Employee`)
		.then((result) => mapForemanWorkItems(db, result?.data))
		.catch(() => undefined)
		.finally(() => {
			cacheRefresh = undefined
			const next = queuedCacheRefresh
			queuedCacheRefresh = undefined
			if (next) refreshForemanBacklogCache(next.id, next.db)
		})
	return cacheRefresh
}

const getForemanWorkItems = async (id: number, db: ForemanDB | null): Promise<Employee> => {
	if (!(await hasConnection()) && db) {
		try {
			return await getForemanWorkItemsFB(db)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get foreman work items from cache' })
			throw error
		}
	}
	const result = await getOne<Employee>(`/Workers/${id}/WorkItems/Backlog/Employee`)
	if (db) {
		mapForemanWorkItems(db, result?.data)
	}
	return result?.data ?? []
}

const getLaborWorkItems = async (id: number, db: ForemanDB | null): Promise<Employee[]> => {
	if (!(await hasConnection()) && db) {
		try {
			return await getLaborWorkItemsFB(db)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get labor work items from cache' })
			throw error
		}
	}
	const result = await getAll<Employee[]>(`/Workers/${id}/WorkItems/Backlog/DirectReports`)
	if (db) {
		mapLaborWorkItems(db, result?.data)
	}
	return result?.data ?? []
}
const assignTask = async (
	taskId: number,
	workerId: number,
	taskType: string,
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await assignTaskFB(db, workerId, taskId, taskType)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to assign task in offline mode' })
			throw error
		}
	}
	const result = await post(
		`/Workers/${workerId}/Tasks/${taskId}?type=${taskType}`,
		`Could not assign task ${taskId} to worker ${workerId}.`,
	)
	return result.success
}

const unassignTask = async (taskId: number, workerId: number, taskType: string, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			return await unassignTaskFB(db, workerId, taskId, taskType)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to unassign task in offline mode' })
			throw error
		}
	}
	const result = await deleteOne(
		`/Workers/${workerId}/Tasks/${taskId}?type=${taskType}`,
		`Could not unassign task ${taskId} from worker ${workerId}.`,
	)
	return result.success
}

const activateTaskStatus = async (workerId: number, taskId: number, taskType: string, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			return await activateTaskFB(db, workerId, taskId, taskType)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to activate task in offline mode' })
			throw error
		}
	}
	const result = await post(
		`/Workers/${workerId}/ActiveTasks/${taskId}?type=${taskType}`,
		`Could not activate task ${taskId} for worker ${workerId}.`,
	)
	return result.success
}

const deactivateTaskStatus = async (workerId: number, taskId: number, taskType: string, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			return await deactivateTaskFB(db, workerId, taskId, taskType)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to deactivate task in offline mode' })
			throw error
		}
	}
	const result = await deleteOne(
		`/Workers/${workerId}/ActiveTasks/${taskId}?type=${taskType}`,
		`Could not activate task ${taskId} for worker ${workerId}.`,
	)
	return result.success
}

const getWorkItemInfo = async (
	workItemIds: number[],
	db: ForemanDB | null,
): Promise<WorkItemInfo[]> => {
	if (!(await hasConnection()) && db) {
		try {
			return await getWorkItemInfoFB(db, workItemIds)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get work item info from cache' })
			throw error
		}
	}
	const result = await getAll<WorkItemInfo[]>(
		`/WorkItems/Info?workItemIds=${workItemIds.join(',')}`,
	)

	return result?.data ?? []
}

export {
	getAssignedForemen as getBacklogForemen,
	getForemanBacklog,
	getForemanBacklogWorkItems,
	getForemanBacklogRoots,
	getForemanBacklogChildren,
	refreshForemanBacklogCache,
	assignTask,
	unassignTask,
	activateTaskStatus,
	deactivateTaskStatus,
	getWorkItemInfo,
	getForemanWorkItems,
	getLaborWorkItems,
}
